import { useState, useEffect } from "react";
import { Control, FieldErrors, useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { AddressSelector } from "../components/contact-form/AddressSelector";
import {
  EMR_CONSENT_TEMPLATE_VERSION,
  EmrAcceptance,
} from "../components/contact-form/EmrAcceptance";
import { ContactFormSchema } from "../components/contact-form/contactFormSchema";
import { useParams } from "react-router-dom";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams";
import { useQuestionnaireAnswersState } from "../components/contact-form/utils/questionnaireStorage";
import { useNavigateToRedirectPage } from "../hooks/useNavigateToRedirectPage";
import {
  mergePartnerConfig,
  usePartnerConfig,
} from "../hooks/usePartnerConfig";
import { captureException } from "@sentry/react";
import { useAnalytics } from "../hooks/useAnalytics";

const PATIENT_UPDATE_APPLICATION_SITE = gql`
  mutation PatientUpdateApplicationSiteEmrConsent(
    $action: PatientUpdateApplicationSiteActionInput!
  ) {
    patientUpdateApplicationSite(action: $action) {
      id
    }
  }
`;

type EmrConsentFormData = {
  address: string;
  zipCode: string;
  emrConsentAccepted: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const noop = (_e: any) => {};

export function EmrConsentPageContent({
  config,
}: {
  config: LandingPageConfig;
}) {
  const { id: landingPageId = "" } = useParams<{ id: string }>();
  const partnerConfig = usePartnerConfig();
  const [questionnaireState, setQuestionnaireState] =
    useQuestionnaireQueryStates();
  const [questionnaireResults] = useQuestionnaireAnswersState();
  const navigateToRedirectPage = useNavigateToRedirectPage();
  const { track } = useAnalytics();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmrConsentFormData>({
    defaultValues: {
      address: "",
      zipCode: "",
      emrConsentAccepted: false,
    },
  });

  const emrConsentAccepted = watch("emrConsentAccepted");
  const [address, setAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [updateApplicationSite] = useMutation(PATIENT_UPDATE_APPLICATION_SITE);

  const applicationSiteId =
    questionnaireState.apiResponse?.outcome?.applicationSiteId;

  useEffect(() => {
    track("lp.emr_consent.viewed", {
      landing_page_id: landingPageId,
      application_site_id: applicationSiteId,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: EmrConsentFormData) => {
    if (!applicationSiteId || !emrConsentAccepted) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await updateApplicationSite({
        variables: {
          action: {
            id: applicationSiteId,
            emrConsent: {
              consentSignedAt: new Date().toISOString(),
              consentTemplateVersion: EMR_CONSENT_TEMPLATE_VERSION,
            },
            setAddress: { value: address || data.address },
          },
        },
      });

      track("lp.emr_consent.submitted", {
        landing_page_id: landingPageId,
        application_site_id: applicationSiteId,
      });
      setQuestionnaireState({
        apiResponse: questionnaireState.apiResponse,
        formData: questionnaireState.formData,
        priorityStudyId: questionnaireState.priorityStudyId,
      });
      navigateToRedirectPage(
        { ...questionnaireState, isAgeEligible: true },
        questionnaireResults,
        landingPageId,
      );
    } catch (error) {
      captureException(error);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white">
        <div className="relative z-10 max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-16 pb-12 md:pb-24">
          <div className="text-center mb-4 md:mb-12">
            <h1 className="text-[1.625rem] leading-[1.3] font-extrabold text-gray-900 sm:text-5xl md:text-6xl px-2 sm:px-4">
              <span className="block text-[1.375rem] md:text-[2rem] bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] overflow-visible">
                Authorize medical records access
              </span>
            </h1>
          </div>

          <div className="w-full max-w-4xl mx-auto px-2">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div className="space-y-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 text-sm">
                  To confirm your eligibility for this trial, we need your
                  permission to access your medical records. We will only access
                  the specific information relevant to the trial eligibility and
                  will keep your data secure and confidential.
                </p>

                <AddressSelector
                  control={control as unknown as Control<ContactFormSchema>}
                  errors={errors as unknown as FieldErrors<ContactFormSchema>}
                  onChange={(addr: string, zipCode: string) => {
                    setAddress(addr);
                    setValue("address", addr);
                    setValue("zipCode", zipCode);
                  }}
                />

                <EmrAcceptance
                  control={control as unknown as Control<ContactFormSchema>}
                  errors={errors as unknown as FieldErrors<ContactFormSchema>}
                  handleInputFocus={noop}
                />
              </div>

              {submitError && (
                <p className="text-red-600 text-sm text-center">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !address || !emrConsentAccepted}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting…" : "Submit authorization"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
